import React, { useEffect, useState } from 'react';
import { getCampaigns, createCampaign, pauseCampaign } from '../../api/campaigns';
import { analytics } from '../../services/analytics';

interface Campaign {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  total_calls: number;
  answer_rate?: number;
  completion_rate?: number;
  created_at: string;
}

interface CampaignFormData {
  name: string;
  phone_numbers_csv: string;
  script: string;
  voice: string;
  schedule: 'now' | 'later';
  scheduled_at: string;
}

const INITIAL_FORM: CampaignFormData = {
  name: '',
  phone_numbers_csv: '',
  script: '',
  voice: 'alloy',
  schedule: 'now',
  scheduled_at: '',
};

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    draft: { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af' },
    running: { bg: 'rgba(99,102,241,0.15)', color: '#818cf8' },
    paused: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    completed: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
  };
  const c = colors[status] ?? colors['draft']!;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: c!.bg, color: c!.color }}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<CampaignFormData>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    try {
      const res = await getCampaigns();
      setCampaigns(res?.campaigns || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.phone_numbers_csv || !form.script) {
      setError('Please fill in name, phone numbers, and script.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const phoneNumbers = form.phone_numbers_csv
        .split(/[,\n]+/)
        .map((n) => n.trim())
        .filter(Boolean);

      const created = await createCampaign({
        name: form.name,
        phone_numbers: phoneNumbers,
        script: form.script,
        voice: form.voice,
        scheduled_at: form.schedule === 'later' ? form.scheduled_at : undefined,
      });
      analytics.track('campaign_created', {
        type: form.schedule,
        contactCount: phoneNumbers.length,
        campaignId: created?.campaign?.id,
      });
      setShowModal(false);
      setForm(INITIAL_FORM);
      fetchCampaigns();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseCampaign(id);
      fetchCampaigns();
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Campaigns</h1>
          <p className="text-gray-400 mt-1">Auto-marketing: call lists of numbers with your AI agent.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all"
          style={{ background: '#6366f1', border: '1px solid rgba(99,102,241,0.5)' }}
        >
          + New Campaign
        </button>
      </div>

      {/* Campaigns list */}
      {campaigns.length === 0 ? (
        <div
          className="rounded-xl p-8 text-center"
          style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="text-gray-400 mb-4">No campaigns yet. Create your first campaign to get started.</p>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-white"
            style={{ background: '#6366f1' }}
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-xl p-4 md:p-5"
              style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-white">{campaign.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">Created {new Date(campaign.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={campaign.status} />
                  {campaign.status === 'running' && (
                    <button
                      onClick={() => handlePause(campaign.id)}
                      className="text-xs px-3 py-1 rounded-lg"
                      style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }}
                    >
                      Pause
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <p className="text-xs text-gray-500">Total Calls</p>
                  <p className="text-lg font-semibold text-white">{campaign.total_calls}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Answer Rate</p>
                  <p className="text-lg font-semibold text-white">{campaign.answer_rate != null ? `${campaign.answer_rate}%` : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completion Rate</p>
                  <p className="text-lg font-semibold text-white">{campaign.completion_rate != null ? `${campaign.completion_rate}%` : '—'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Campaign Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div
            className="w-full max-w-lg mx-4 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">New Campaign</h2>
              <button onClick={() => { setShowModal(false); setError(null); }} className="text-gray-400 hover:text-white text-xl">&times;</button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(248,113,113,0.1)', color: '#f87171', border: '1px solid rgba(248,113,113,0.2)' }}>
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Campaign Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="e.g. Spring Promo 2026"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Phone Numbers (CSV or one per line)</label>
                <textarea
                  value={form.phone_numbers_csv}
                  onChange={(e) => setForm({ ...form, phone_numbers_csv: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 resize-y"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="+12125551234&#10;+14155559876"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Script <span className="text-gray-600">(supports {'{{variable}}'} placeholders)</span>
                </label>
                <textarea
                  value={form.script}
                  onChange={(e) => setForm({ ...form, script: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 resize-y"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="Hi {{name}}, this is a call from..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Voice</label>
                <select
                  value={form.voice}
                  onChange={(e) => setForm({ ...form, voice: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-white"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <option value="alloy">Alloy</option>
                  <option value="echo">Echo</option>
                  <option value="fable">Fable</option>
                  <option value="onyx">Onyx</option>
                  <option value="nova">Nova</option>
                  <option value="shimmer">Shimmer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Schedule</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="schedule"
                      checked={form.schedule === 'now'}
                      onChange={() => setForm({ ...form, schedule: 'now' })}
                      className="accent-indigo-500"
                    />
                    Start now
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="schedule"
                      checked={form.schedule === 'later'}
                      onChange={() => setForm({ ...form, schedule: 'later' })}
                      className="accent-indigo-500"
                    />
                    Schedule for later
                  </label>
                </div>
                {form.schedule === 'later' && (
                  <input
                    type="datetime-local"
                    value={form.scheduled_at}
                    onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                    className="mt-2 w-full px-3 py-2.5 rounded-lg text-sm text-white"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setError(null); }}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#d1d5db', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white"
                style={{ background: '#6366f1', border: '1px solid rgba(99,102,241,0.5)' }}
              >
                {submitting ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
